import { useEffect, useRef } from 'react'
import type { Device } from '../lib/types'
import { customerColor } from '../lib/remote'

interface Props {
  device: Device
  kind: 'ssh' | 'gui'
  color?: string
  busy?: boolean
  error?: string | null
  onConfirm: () => void
  onClose: () => void
}

/**
 * Der Verbindungsdialog vor jedem Fernzugriff.
 *
 * Er zeigt in klarer Sprache, zu welchem Kunden das Gerät gehört – ein
 * Fehlgriff auf das falsche Kundennetz ist der teuerste Fehler in diesem
 * Werkzeug (docs/sicherheit.md) – und was aufgezeichnet wird.
 */
export function VerbindungsDialog({ device, kind, color, busy, error, onConfirm, onClose }: Props) {
  const confirmRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    confirmRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const address =
    kind === 'ssh'
      ? `${device.mgmt_ip}:${device.ssh_port}`
      : `${device.gui_scheme}://${device.mgmt_ip}:${device.gui_port}`

  return (
    <div className="overlay" onMouseDown={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="verbindung-titel"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="kennfarbe" style={{ background: customerColor(color) }} aria-hidden="true" />
        <div className="dialog-body">
          <h2 id="verbindung-titel" className="card-title">
            {kind === 'ssh' ? 'SSH-Terminal öffnen' : 'Web-GUI öffnen'}
          </h2>

          <dl className="dialog-facts">
            <dt>Kunde</dt>
            <dd>
              <span
                className="dot"
                style={{ background: customerColor(color), width: 8, height: 8 }}
                aria-hidden="true"
              />{' '}
              {device.customer_name ?? 'unbekannt'}
            </dd>
            <dt>Standort</dt>
            <dd>{device.site_name ?? 'unbekannt'}</dd>
            <dt>Gerät</dt>
            <dd className="mono">{device.name}</dd>
            <dt>Ziel</dt>
            <dd className="mono">{address}</dd>
            <dt>Weg</dt>
            <dd>über den Agent {device.agent_name || 'am Standort'}, keine Portfreigabe</dd>
          </dl>

          <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
            Die Zugangsdaten kommen aus dem Tresor und werden beim Verbinden eingesetzt; das
            Kennwort wird nicht angezeigt.{' '}
            {kind === 'ssh'
              ? 'Die Sitzung wird vollständig aufgezeichnet.'
              : 'Start und Ende der Sitzung werden protokolliert.'}
          </p>

          {error ? (
            <p className="badge badge-crit" role="alert" style={{ height: 'auto', padding: '8px 10px' }}>
              {error}
            </p>
          ) : null}

          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn" onClick={onClose}>
              Abbrechen
            </button>
            <button
              type="button"
              className="btn btn-primary"
              ref={confirmRef}
              disabled={busy}
              onClick={onConfirm}
            >
              {busy ? 'Verbinde …' : 'Verbinden'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
